import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { removeGroupMember, validateApiResponse } from '../../../services';
import { COMMIT_BACKSTOP_MS, UNDO_WINDOW_MS } from '../constants/groupMembers';

/**
 * Remove a member from a group: immediate and optimistic, with an Undo toast and a DELAYED
 * commit. The row leaves the table at once (`hideMember`); the removal request goes out when the
 * toast closes (expiry or manual close) unless Undo was pressed (`showMember`, which cannot fail:
 * it is local). A committed removal is final (`dropMember`); a failed request brings the row back.
 *
 * Toast contract: the message includes the member name, and the e-mail too when another removal
 * with the same name is still pending (the provider drops an identical variant+message toast
 * created within 2 s, which would leave the second member without Undo). `onAction` does not
 * close the toast so it closes it itself, and `onClose` (fires on expiry AND on close) commits
 * unless `undone`.
 *
 * Leaving the page inside the Undo window closes the open toasts, which confirms those removals:
 * the provider outlives the page, so they would be committed anyway, and doing it now keeps the
 * next load of the group consistent. A browser reload or tab close inside the window sends nothing.
 *
 * @param {object} options
 * @param {string} options.groupId
 * @param {(id: string) => void} options.hideMember `useGroup`'s.
 * @param {(id: string) => void} options.showMember `useGroup`'s.
 * @param {(id: string) => void} options.dropMember `useGroup`'s.
 *
 * The LAST member goes the same way as any other: no dialog, the toast offers Undo (CT-5841).
 *
 * @returns {{ removeMember: (member: object) => void }}
 */
export const useGroupMemberRemoval = ({ groupId, hideMember, showMember, dropMember }) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const toastRef = useRef(toast);
  const activeRef = useRef(new Map());
  const messagesRef = useRef(new Set());

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    const active = activeRef.current;
    return () => {
      active.forEach(({ toastId }) => toastRef.current.close(toastId));
    };
  }, []);

  const failRemoval = useCallback(
    (memberId, err) => {
      showMember(memberId);
      toastRef.current.error({
        title: t('groups.removeMember.errorTitle'),
        message: err?.message ?? t('groups.errors.generic'),
        canClose: true,
      });
    },
    [showMember, t],
  );

  const removeMember = useCallback(
    (member) => {
      let undone = false;
      let settled = false;
      let backstop = null;
      let toastId = null;
      const baseMessage = t('groups.removeMember.message', { name: member.name });
      const message = messagesRef.current.has(baseMessage)
        ? t('groups.removeMember.messageWithEmail', { name: member.name, email: member.email })
        : baseMessage;
      messagesRef.current.add(message);

      const settle = () => {
        settled = true;
        window.clearTimeout(backstop);
        messagesRef.current.delete(message);
        activeRef.current.delete(member.id);
      };

      const commit = async () => {
        if (settled || undone) return;
        settle();
        try {
          validateApiResponse(
            await removeGroupMember(groupId, member.id),
            t('groups.removeMember.errorMessage'),
          );
          dropMember(member.id);
        } catch (err) {
          failRemoval(member.id, err);
        }
      };

      hideMember(member.id);

      toastId = toastRef.current.info({
        title: t('groups.removeMember.title'),
        message,
        action: t('groups.removeMember.undo'),
        duration: UNDO_WINDOW_MS,
        onAction: () => {
          if (settled) return;
          undone = true;
          settle();
          showMember(member.id);
          toastRef.current.close(toastId);
        },
        onClose: commit,
      });
      activeRef.current.set(member.id, { toastId });
      backstop = window.setTimeout(commit, UNDO_WINDOW_MS + COMMIT_BACKSTOP_MS);
    },
    [dropMember, failRemoval, groupId, hideMember, showMember, t],
  );

  return useMemo(() => ({ removeMember }), [removeMember]);
};
